import React, { useMemo, useEffect } from "react";
import { useField } from "formik";
import { NormalizedDescriptionInfo } from "@common/types/normalizedDescription";
import { TransferRuleDirection } from "@common/types/transactionRule";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { TriggerButtonProps } from "@design-system/overlay/triggerButtonProps";
import { useNormalizedDescriptions } from "@hooks/useNormalizedDescriptions";
import { TransactionRuleConditionFormSchema } from "../constants";
import { getFieldNamePrefix } from "../../utils";

interface Props {
    index: number;
    transferDirection?: TransferRuleDirection;
    controlButtonSize?: TriggerButtonProps["size"];
    normalizedDescriptions?: NormalizedDescriptionInfo[];
}

export const SimilarToConditionForm: React.FC<Props> = ({
    index,
    transferDirection,
    controlButtonSize,
    normalizedDescriptions: fallbackNormalizedDescriptions,
}) => {
    const fieldName = `${getFieldNamePrefix(index, transferDirection)}.normalizedDescriptionId`;
    const [field, , helpers] =
        useField<TransactionRuleConditionFormSchema["normalizedDescriptionId"]>(
            fieldName,
        );

    const { data, isLoading } = useNormalizedDescriptions();
    const normalizedDescriptions = data?.normalizedDescriptions;
    const mergedToCanonicalMap = data?.mergedToCanonicalMap;

    useEffect(() => {
        if (!field.value || !mergedToCanonicalMap) {
            return;
        }
        const canonicalId = mergedToCanonicalMap[field.value];
        if (canonicalId) {
            helpers.setValue(canonicalId);
        }
    }, [field.value, mergedToCanonicalMap, helpers]);

    const items = useMemo(() => {
        const ids: string[] = [];
        const seenIds = new Set<string>();
        const addId = (id: string) => {
            if (!seenIds.has(id)) {
                seenIds.add(id);
                ids.push(id);
            }
        };

        for (const desc of normalizedDescriptions ?? []) {
            addId(desc.normalizedDescriptionId);
        }
        for (const desc of fallbackNormalizedDescriptions ?? []) {
            addId(desc.id);
            const canonicalId = mergedToCanonicalMap?.[desc.id];
            if (canonicalId) {
                addId(canonicalId);
            }
        }

        return ids;
    }, [
        normalizedDescriptions,
        fallbackNormalizedDescriptions,
        mergedToCanonicalMap,
    ]);

    const labelMap = useMemo(() => {
        const map = new Map<string, string>();
        for (const desc of fallbackNormalizedDescriptions ?? []) {
            map.set(desc.id, desc.normalizedDescription);
            const canonicalId = mergedToCanonicalMap?.[desc.id];
            if (canonicalId) {
                map.set(canonicalId, desc.normalizedDescription);
            }
        }
        for (const desc of normalizedDescriptions ?? []) {
            map.set(
                desc.normalizedDescriptionId,
                desc.sampleBankDescription ?? desc.normalizedDescription,
            );
        }
        return map;
    }, [
        normalizedDescriptions,
        fallbackNormalizedDescriptions,
        mergedToCanonicalMap,
    ]);

    const pickerItems = useMemo(
        () => items.map((id) => ({ value: id, label: labelMap.get(id) ?? id })),
        [items, labelMap],
    );

    return (
        <Picker.Select
            items={pickerItems}
            searchable
            value={field.value === "" ? null : (field.value ?? null)}
            onValueChange={(v) => helpers.setValue(v ?? "")}
            disabled={isLoading}
            placeholder="Select a description group..."
            searchPlaceholder="Search description groups..."
            emptyLabel="No matching descriptions"
            trigger={
                <Picker.Button
                    variant="primary"
                    size={controlButtonSize ?? "md"}
                />
            }
        />
    );
};
